// /src/components/imovel/BannerPlayer.jsx
// VERSÃO 1.0 SUPREMA - TIM UNITY BANNER PLAYER (CINEMATIC SLIDER)

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiZap, FiExternalLink } from 'react-icons/fi';
import apiClient from '../../api/apiClient';

const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1920x600?text=AGUARDANDO+MIDIA';
    if (path.startsWith('http')) return path;
    const baseUrl = import.meta.env.VITE_API_BASE_URL 
        ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') 
        : ''; 
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

export default function BannerPlayer({ posicao = 'home_topo' }) {
    const [banners, setBanners] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadBanners = async () => {
            try {
                const res = await apiClient.get('/api/banners/public');
                // Filtra pela posição solicitada (ex: topo, meio)
                const filtered = res.data.filter(b => b.posicao === posicao);
                setBanners(filtered);
            } catch (e) {
                console.error("💥 Falha no downlink de banners:", e);
            } finally {
                setLoading(false);
            }
        };
        loadBanners();
    }, [posicao]);

    // Auto-play: Troca a cada 8 segundos
    useEffect(() => {
        if (banners.length <= 1) return;
        const timer = setInterval(() => next(), 8000);
        return () => clearInterval(timer);
    }, [currentIndex, banners]);

    const next = () => setCurrentIndex((prev) => (prev + 1) % banners.length);
    const prev = () => setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));

    if (loading || banners.length === 0) return null;

    const currentBanner = banners[currentIndex];

    return (
        <section className="relative w-full h-[400px] md:h-[600px] lg:h-[700px] overflow-hidden bg-black">
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentBanner._id}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 1.2, ease: "circOut" }}
                    className="absolute inset-0"
                >
                    {/* A imagem do Banner */}
                    <img 
                        src={resolveImagePath(currentBanner.imagemUrl)} 
                        alt={currentBanner.titulo}
                        className="w-full h-full object-cover opacity-80"
                    />
                    
                    {/* Overlays de Design MOS */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] via-transparent to-transparent opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

                    {/* Conteúdo flutuante (Opcional, se o banner não tiver texto na imagem) */}
                    <div className="absolute inset-0 flex items-center">
                        <div className="container mx-auto px-8 md:px-16">
                            <motion.div 
                                initial={{ x: -50, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="max-w-2xl space-y-6"
                            >
                                {currentBanner.link && (
                                    <a 
                                        href={currentBanner.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-accent/20 border border-accent/30 text-accent text-[10px] font-black uppercase tracking-[0.3em] backdrop-blur-md hover:bg-accent hover:text-black transition-all"
                                    >
                                        <FiZap className="animate-pulse" /> Oferta Ativa
                                    </a>
                                )}
                                <h2 className="text-5xl md:text-8xl font-black text-white uppercase tracking-[ -0.05em] leading-[0.85] drop-shadow-2xl">
                                    {currentBanner.titulo}
                                </h2>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Controles de Navegação Elite */}
            {banners.length > 1 && (
                <>
                    <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
                        <NavButton icon={FiChevronLeft} onClick={prev} />
                        <NavButton icon={FiChevronRight} onClick={next} />
                    </div>

                    {/* Indicadores de Progresso */}
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-3 z-30">
                        {banners.map((_, i) => (
                            <button 
                                key={i}
                                onClick={() => setCurrentIndex(i)}
                                className={`h-1 rounded-full transition-all duration-500 ${i === currentIndex ? 'w-12 bg-accent' : 'w-4 bg-white/20'}`}
                                style={i === currentIndex ? { boxShadow: '0 0 15px var(--accent-color)' } : {}}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

const NavButton = ({ icon: Icon, onClick }) => (
    <button 
        onClick={onClick}
        className="p-5 rounded-full border border-white/10 bg-black/20 backdrop-blur-md text-white hover:bg-accent hover:text-black hover:border-accent transition-all duration-300 pointer-events-auto active:scale-90 shadow-2xl"
    >
        <Icon size={24} />
    </button>
);